<template>
  <LoadingWrapper :state="status">
    <SideBarLayoutContentHero>
      <template #hero>
        <div class="grid w-full grid-cols-1 gap-2 md:grid-cols-2">
          <BaseCard class="flex flex-col p-6">
            <div class="flex flex-row">
              <BackButton backup-route-name="coach-surveys" size="XS" class="mr-1 -ml-3" />
              <div class="flex flex-col">
                <p v-if="survey.title" class="text-body-medium text-on-surface-variant truncate">
                  {{ survey.feedbackTopic.title }}
                </p>
                <h1 class="text-headline-large leading-tight">
                  {{ survey.title ?? survey.feedbackTopic.title }}
                </h1>
              </div>
            </div>
            <span class="text-body-medium text-on-surface-variant mb-2">
              <span v-if="survey.status === SurveyState.finished">{{
                $t('survey.state.finished')
              }}</span>
            </span>
            <div class="text-on-surface-variant mb-3 flex items-center justify-between gap-2">
              <CoachSurveyRecurringInformation
                :recurrence="survey.recurrence"
                :next-occurrence-at="survey.nextOccurrenceAt"
                :createdAt="survey.createdAt"
                :numberOfSurveys="survey.analysis.surveys.length"
              />
            </div>
            <div>
              <BaseButtonGroup size="M">
                <BaseComputedButtonGroup
                  class="flex w-full flex-row justify-end"
                  size="M"
                  menu-button-color="tonal"
                  :buttons="[
                    {
                      id: 'editTitleAndMessage',
                      flexibelConfigurations: [{ type: 'menu', order: 2 }],
                      label: $t('survey.editTitleAndMessage'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'edit',
                      callback: openEditTitleAndMessageDialog,
                    },
                    {
                      id: 'editMembers',
                      flexibelConfigurations: [{ type: 'menu', order: 2 }],
                      label: $t('survey.editMembers'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'group_add',
                      callback: openEditMembersDialog,
                    },
                    {
                      id: 'editReccurence',
                      flexibelConfigurations: [{ type: 'menu', order: 2 }],
                      label: $t('survey.editReccurence'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'edit_calendar',
                      callback: openEditReccurenceDialog,
                    },
                    {
                      id: 'list',
                      flexibelConfigurations: [{ type: 'menu' }],
                      label: $t('survey.showQuestions'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'list',
                      callback: openShowQuestionsDialog,
                    },
                    {
                      id: 'delete',
                      flexibelConfigurations: [{ type: 'menu', order: 1 }],
                      label: $t('common.delete'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'delete',
                      destructive: true,
                      callback: openDeleteSurveyDialog,
                    },
                  ]"
                />
              </BaseButtonGroup>
            </div>
          </BaseCard>
          <BaseCard class="grid grid-cols-2 gap-4 p-6">
            <div class="col-span-2 flex w-full flex-row items-center justify-between gap-2">
              <div class="flex w-full flex-row items-center gap-2">
                <BaseIcon class="text-on-surface-variant mr-2">people</BaseIcon>
                <div class="flex flex-col">
                  <BaseMemberAvatars
                    class="bg-surface-container"
                    :members="survey.surveyTemplateMembers"
                  />
                  <span class="text-body-medium text-on-surface-variant">Mitglieder</span>
                </div>
              </div>
            </div>
            <div class="flex w-full flex-row items-center gap-2">
              <BaseIcon class="text-on-surface-variant mr-2">chat</BaseIcon>
              <div class="flex flex-col">
                <span class="text-display-small font-medium">{{ totalNumberOfAnswers }}</span>
                <span class="text-body-medium text-on-surface-variant -mt-2">Antworten</span>
              </div>
            </div>
            <div class="flex w-full flex-row items-center gap-2">
              <BaseIcon class="text-on-surface-variant mr-2">ballot</BaseIcon>
              <div class="flex flex-col">
                <span class="text-display-small font-medium">{{
                  survey.analysis.surveys.length
                }}</span>
                <span class="text-body-medium text-on-surface-variant -mt-2">Durchführungen</span>
              </div>
            </div>
          </BaseCard>
        </div>
      </template>
      <JournalAnalysis
        :individual-values="survey.analysis.individualValues"
        :surveys="survey.analysis.surveys"
        :scale="survey.analysis.scale"
        :questions="survey.analysis.questions"
        :selectable="true"
      />
    </SideBarLayoutContentHero>
    <EditCoachSurveyMembersDialog
      :is-open="isEditMembersDialogOpen"
      :survey="survey"
      @close-dialog="closeEditMembersDialog"
      @survey-updated="reload"
    />
    <EditCoachSurveyReccurenceDialog
      :is-open="isEditReccurenceDialogOpen"
      :survey="survey"
      @close-dialog="closeEditReccurenceDialog"
      @survey-updated="reload"
    />
    <EditCoachSurveyTitleAndMessageDialog
      :is-open="isEditTitleAndMessageDialogOpen"
      :survey="survey"
      @close-dialog="closeEditTitleAndMessageDialog"
      @survey-updated="reload"
    />
    <DeleteSurveyDialog
      :is-open="isDeleteSurveyDialogOpen"
      @close="closeDeleteSurveyDialog"
      @delete-survey="clickDeleteSurvey"
    />
    <AllQuestionsDialog
      v-if="isShowQuestionsDialogOpen"
      :is-open="isShowQuestionsDialogOpen"
      :feedback-topics="[survey.feedbackTopic]"
      @close="closeShowQuestionsDialog"
    />
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import SideBarLayoutContentHero from '@/core/components/layouts/SideBarLayoutContentHero.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import { SurveyState } from '@/modules/survey/models/survey-state'
  import { useCoachSurvey } from '../composables/use-coach-survey'
  import JournalAnalysis from '@/core/components/journal-analysis/JournalAnalysis.vue'
  import BaseMemberAvatars from '@/core/components/base/avatar/BaseMemberAvatars.vue'
  import BaseCard from '@/core/components/base/card/BaseCard.vue'
  import BaseComputedButtonGroup from '@/core/components/base/button/BaseComputedButtonGroup.vue'
  import BaseButtonGroup from '@/core/components/base/button/BaseButtonGroup.vue'
  import EditCoachSurveyMembersDialog from '../components/EditCoachSurveyMembersDialog.vue'
  import EditCoachSurveyReccurenceDialog from '../components/EditCoachSurveyReccurenceDialog.vue'
  import EditCoachSurveyTitleAndMessageDialog from '../components/EditCoachSurveyTitleAndMessageDialog.vue'
  import DeleteSurveyDialog from '@/modules/survey/components/DeleteSurveyDialog.vue'
  import AllQuestionsDialog from '@/modules/survey/components/AllQuestionsDialog.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import CoachSurveyRecurringInformation from '../components/CoachSurveyRecurringInformation.vue'
  import BackButton from '@/core/components/BackButton.vue'

  const { survey, status, reload, deleteSurvey } = useCoachSurvey()

  const totalNumberOfAnswers = computed(() => {
    return (
      survey.value?.analysis.individualValues.reduce(
        (acc, curr) => acc + curr.feedbacks.length,
        0,
      ) ?? 0
    )
  })

  const {
    isOpen: isEditMembersDialogOpen,
    openDialog: openEditMembersDialog,
    closeDialog: closeEditMembersDialog,
  } = useDialog()

  const {
    isOpen: isEditReccurenceDialogOpen,
    openDialog: openEditReccurenceDialog,
    closeDialog: closeEditReccurenceDialog,
  } = useDialog()

  const {
    isOpen: isEditTitleAndMessageDialogOpen,
    openDialog: openEditTitleAndMessageDialog,
    closeDialog: closeEditTitleAndMessageDialog,
  } = useDialog()

  const {
    isOpen: isDeleteSurveyDialogOpen,
    openDialog: openDeleteSurveyDialog,
    closeDialog: closeDeleteSurveyDialog,
  } = useDialog()

  const {
    isOpen: isShowQuestionsDialogOpen,
    openDialog: openShowQuestionsDialog,
    closeDialog: closeShowQuestionsDialog,
  } = useDialog()

  const clickDeleteSurvey = () => {
    closeDeleteSurveyDialog()
    deleteSurvey()
  }
</script>
